<template>
  <div>
    <b-navbar toggleable="lg" variant="primary" class="modern-navbar">
      <div class="navbar-left">
        <b-button class="sidebar-toggle" v-b-toggle.sidebar-menu variant="outline-light" size="sm">
          <b-icon icon="list" variant="light"></b-icon>
        </b-button>

        <div class="logo-container" @click="redirect('/home')">
          <img class="logo-image" alt="logo" src="@/assets/logo_express_white.png" />
          <div class="logo-glow"></div>
        </div>
      </div>

      <div class="navbar-center">
        <div class="calendar-widget">
          <div class="calendar-content">
            <div class="date-display">
              <span class="day-number">{{ this.momentInstance.format('DD') }}</span>
              <img src="@/assets/calendar.png" alt="calendar" class="calendar-icon" />
            </div>
            <div class="date-details">
              <div class="month-year">{{ this.momentInstance.format('[de] MMMM [de] YYYY') }}</div>
              <div class="time-day">{{ this.momentInstance.format('dddd [-] HH:mm:ss') }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="navbar-right">
        <div class="user-profile-container">
          <div class="user-profile" @click="toggleUserMenu">
            <div class="user-avatar">
              <i class="fa fa-user-circle"></i>
            </div>
            <div class="user-info">
              <div class="user-name">{{ user }}</div>
              <div class="user-details">
                <span class="user-type">{{ userType }}</span>
                <span class="separator">•</span>
                <span class="division-name">{{ divisionName }}</span>
              </div>
            </div>
            <div class="dropdown-arrow" :class="{ rotated: showUserMenu }">
              <i class="fa fa-chevron-down"></i>
            </div>
          </div>

          <!-- Menu dropdown do usuário -->
          <transition name="dropdown">
            <div v-if="showUserMenu" class="user-dropdown-menu">
              <!-- Resumo do usuário (exibido no mobile, já que ocultamos o bloco no topo) -->
              <div class="dropdown-user-brief">
                <div class="brief-info">
                  <div class="brief-name">{{ user }}</div>
                  <div class="brief-details">{{ userType }} • {{ divisionName }}</div>
                </div>
              </div>

              <div class="dropdown-item" @click="goToProfile">
                <i class="fa fa-user"></i>
                <span>Perfil</span>
              </div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-item" @click="logout">
                <i class="fa fa-sign-out-alt"></i>
                <span>Sair</span>
              </div>
            </div>
          </transition>
        </div>

        <!-- Notifications dropdown (rightmost) -->
        <div class="notification-wrapper">
          <div
            class="notification-bell"
            :class="{ 'has-notifications': totalNotifications > 0 }"
            v-b-popover.hover.bottom="'Notificações! Clique para ver'"
            role="button"
            :aria-label="`Notificações: ${totalNotifications}`"
            @click.stop="toggleNotifications"
          >
            <i class="fa fa-bell"></i>
            <span v-if="totalNotifications > 0" class="badge" aria-live="polite">{{ totalNotifications }}</span>
          </div>
          <div v-if="showBellToast" class="bell-toast" role="status" aria-live="polite">
            <i class="fas fa-exclamation-triangle notification-icon"></i>
            <span> Você possui notificações não lidas </span>
          </div>
          <transition name="dropdown">
            <div v-if="showNotifications" class="notifications-dropdown">
              <div class="notifications-header">Notificações</div>
              <div v-if="posOfflineCount > 0" class="notifications-item" @click.stop="openPosOffline">
                <div class="item-left">
                  <i class="fas fa-exclamation-triangle"></i>
                </div>
                <div class="item-right">
                  <div class="item-title">Loja Offline</div>
                  <div class="item-subtitle">{{ posOfflineText }}</div>
                </div>
              </div>
              <div v-if="posOfflineCount > 0 && (localServerOfflineCount > 0 || totalPendingCount > 0)" class="dropdown-divider"></div>
              <div v-if="localServerOfflineCount > 0" class="notifications-item" @click.stop="openLocalServer">
                <div class="item-left">
                  <i class="fas fa-database"></i>
                </div>
                <div class="item-right">
                  <div class="item-title">Servidor Local Offline</div>
                  <div class="item-subtitle">{{ localServerOfflineText }}</div>
                </div>
              </div>
              <div v-if="localServerOfflineCount > 0 && totalPendingCount > 0" class="dropdown-divider"></div>
              <div v-if="totalPendingCount > 0" class="notifications-item" @click.stop="openPendencias">
                <div class="item-left">
                  <i class="fas fa-tasks"></i>
                </div>
                <div class="item-right">
                  <div class="item-title">Pendências</div>
                  <div class="item-subtitle">{{ pendingText }}</div>
                </div>
              </div>
              <div v-if="totalNotifications === 0" class="notifications-empty">
                <div class="empty-icon">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="empty-text">Todas as notificações foram visualizadas</div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </b-navbar>
  </div>
</template>
<script>
import moment from 'moment'
import 'moment/locale/pt-br'

export default {
  name: 'NavBar',

  data() {
    return {
      user: '',
      userType: '',
      divisionName: '',
      momentInstance: moment(),
      showUserMenu: false,
      posOfflineCount: 0,
      totalPendingCount: 0,
      localServerOfflineCount: 0,
      showNotifications: false,
      showBellToast: false,
      bellToastTimer: null,
      bellReminderTimer: null
    }
  },

  computed: {
    totalNotifications() {
      const hasPos = Number(this.posOfflineCount || 0) > 0 ? 1 : 0
      const hasPend = Number(this.totalPendingCount || 0) > 0 ? 1 : 0
      const hasLocal = Number(this.localServerOfflineCount || 0) > 0 ? 1 : 0
      return hasPos + hasPend + hasLocal
    },

    posOfflineText() {
      const count = this.posOfflineCount || 0
      return count === 1 ? `Existe ${count} Loja offline` : `Existem ${count} Lojas offline`
    },

    localServerOfflineText() {
      const count = this.localServerOfflineCount || 0
      return count === 1 ? `Existe ${count} servidor local offline` : `Existem ${count} servidores locais offline`
    },

    pendingText() {
      const count = this.totalPendingCount || 0
      return count === 1 ? `Existe ${count} Pendência` : `Existem ${count} Pendências`
    }
  },

  methods: {
    async redirect(ref) {
      // Para navegação do logo, usar o método de limpeza inteligente
      this.clearLocalStorageSavedPages()
      this.$router.push({ path: ref }).catch(() => {})
    },

    clearLocalStorageSavedPages() {
      const preservedWelcome = localStorage.getItem('welcomeModalViewedVersions')
      const editUserMode = localStorage.getItem('editUserMode')

      if (editUserMode) {
        localStorage.removeItem('currentPageUsers')
        localStorage.removeItem('selectedOptionFilterUser')
        localStorage.removeItem('filterUser')
        localStorage.removeItem('filterUserStatus')
      } else {
        localStorage.clear()
      }

      if (preservedWelcome !== null) {
        localStorage.setItem('welcomeModalViewedVersions', preservedWelcome)
      }
    },

    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu
    },

    async goToProfile() {
      this.showUserMenu = false

      // Buscar dados do usuário logado diretamente do sessionStorage
      const userId = sessionStorage.getItem('idUsuarioLogado')
      const userName = sessionStorage.getItem('usuarioLogado')
      const userType = sessionStorage.getItem('typeUser')
      const divisionId = sessionStorage.getItem('divisionId')
      const divisionName = sessionStorage.getItem('divisionName')
      const userEmail = sessionStorage.getItem('userEmail')
      const userPhone = sessionStorage.getItem('userPhone')
      const userIsSlackNotify = sessionStorage.getItem('userIsSlackNotify')
      const userIsTelegramNotify = sessionStorage.getItem('userIsTelegramNotify')
      const userIsAppNotify = sessionStorage.getItem('userIsAppNotify')
      const userIsWhatsAppNotify = sessionStorage.getItem('userIsWhatsAppNotify')

      if (userId) {
        // Salvar dados no localStorage para a tela de edição
        localStorage.setItem('editUserId', userId)
        localStorage.setItem('editUserName', userName)
        localStorage.setItem('editUserType', userType)
        localStorage.setItem('editDivisionId', divisionId)
        localStorage.setItem('editDivisionName', divisionName)
        localStorage.setItem('editUserMode', 'profile')

        // Salvar dados adicionais se disponíveis
        localStorage.setItem('editUserEmail', userEmail || '')
        localStorage.setItem('editUserPhone', userPhone || '')
        localStorage.setItem('editUserSlackNotify', userIsSlackNotify)
        localStorage.setItem('editUserTelegramNotify', userIsTelegramNotify)
        localStorage.setItem('editUserAppNotify', userIsAppNotify)
        localStorage.setItem('editUserWhatsAppNotify', userIsWhatsAppNotify)
      }

      // Evita navegação redundante quando já estamos na tela de perfil
      if (this.$route && this.$route.path !== '/users-manager') {
        this.$router.push('/users-manager').catch(() => {})
      }
    },

    logout() {
      this.showUserMenu = false
      this.clearLocalStorageSavedPages()
      sessionStorage.removeItem('usuarioLogado')
      sessionStorage.removeItem('typeUser')
      sessionStorage.removeItem('divisionName')
      this.$router.push('/login')
    },

    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showUserMenu = false
        this.showNotifications = false
      }
    },

    handlePosOfflineEvent(e) {
      const fromEvent = e && typeof e.detail !== 'undefined' ? Number(e.detail) : null
      const fromStorage = Number(localStorage.getItem('posOfflineCount') || 0)
      this.posOfflineCount = Number.isFinite(fromEvent) ? fromEvent : fromStorage
    },

    handleTotalPendingEvent(e) {
      const fromEvent = e && typeof e.detail !== 'undefined' ? Number(e.detail) : null
      const fromStorage = Number(localStorage.getItem('totalPendingCount') || 0)
      this.totalPendingCount = Number.isFinite(fromEvent) ? fromEvent : fromStorage
    },

    handleLocalServerOfflineEvent(e) {
      const fromEvent = e && typeof e.detail !== 'undefined' ? Number(e.detail) : null
      const fromStorage = Number(localStorage.getItem('localServerOfflineCount') || 0)
      this.localServerOfflineCount = Number.isFinite(fromEvent) ? fromEvent : fromStorage
    },

    toggleNotifications() {
      // Fecha o dropdown do usuário se estiver aberto
      this.showUserMenu = false
      // Alterna o menu de notificações
      this.showNotifications = !this.showNotifications
    },

    closeNotificationsThen(callback) {
      // Fecha o dropdown e só executa o callback após o fim da transição/ani.
      this.showNotifications = false
      this.$nextTick(() => {
        const dropdown = this.$el.querySelector('.notifications-dropdown')
        if (dropdown) {
          const onEnd = () => {
            dropdown.removeEventListener('transitionend', onEnd)
            dropdown.removeEventListener('animationend', onEnd)
            if (typeof callback === 'function') {
              // Executa no próximo tick para garantir DOM estável
              this.$nextTick(callback)
            }
          }
          dropdown.addEventListener('transitionend', onEnd, { once: true })
          dropdown.addEventListener('animationend', onEnd, { once: true })
          // Fallback de segurança caso o evento não dispare
          setTimeout(() => {
            if (typeof callback === 'function') {
              this.$nextTick(callback)
            }
          }, 600)
        } else if (typeof callback === 'function') {
          this.$nextTick(callback)
        }
      })
    },

    openPosOffline() {
      localStorage.setItem('homeSortOption', 'site_offline')
      this.closeNotificationsThen(() => {
        if (this.$route && this.$route.path === '/home') {
          if (typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('home-apply-sort', { detail: 'site_offline' }))
          }
        } else {
          this.redirect('/home')
        }
      })
    },

    openPendencias() {
      this.closeNotificationsThen(() => {
        if (this.$route && this.$route.path === '/home') {
          if (typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('home-open-pending'))
          }
        } else {
          localStorage.setItem('homeOpenPending', '1')
          this.redirect('/home')
        }
      })
    },

    openLocalServer() {
      localStorage.setItem('homeSortOption', 'server_offline')
      this.closeNotificationsThen(() => {
        if (this.$route && this.$route.path === '/home') {
          if (typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('home-apply-sort', { detail: 'server_offline' }))
          }
        } else {
          this.redirect('/home')
        }
      })
    }
  },

  mounted() {
    setInterval(() => {
      this.momentInstance = moment()
    }, 1000)

    // Fechar o menu quando clicar fora
    document.addEventListener('click', this.handleClickOutside)

    // Inicializa contador a partir do localStorage e escuta eventos da Home
    try {
      this.posOfflineCount = Number(localStorage.getItem('posOfflineCount') || 0)
      this.totalPendingCount = Number(localStorage.getItem('totalPendingCount') || 0)
      this.localServerOfflineCount = Number(localStorage.getItem('localServerOfflineCount') || 0)
    } catch (e) {
      // noop
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pos-offline-count', this.handlePosOfflineEvent)
      window.addEventListener('home-total-pending', this.handleTotalPendingEvent)
      window.addEventListener('home-local-server-offline', this.handleLocalServerOfflineEvent)
      window.addEventListener('navbar-open-notifications', () => {
        this.showUserMenu = false
        this.showNotifications = true
      })
      window.addEventListener('navbar-bell-hint', () => {
        // Aplica um efeito de atenção ao sino por alguns segundos
        const bell = this.$el.querySelector('.notification-bell')
        if (bell) {
          bell.classList.add('bell-hint')
          setTimeout(() => bell.classList.remove('bell-hint'), 3000)
        }
        // Exibir pequeno alerta "saindo" do sino
        this.showBellToast = true
        if (this.bellToastTimer) clearTimeout(this.bellToastTimer)
        this.bellToastTimer = setTimeout(() => {
          this.showBellToast = false
          this.bellToastTimer = null
        }, 3500)
      })

      // Caso a Home tenha disparado antes da navbar montar
      try {
        const pendingBellHint = localStorage.getItem('navbarBellHint') === '1'
        if (pendingBellHint) {
          localStorage.removeItem('navbarBellHint')
          const event = new Event('navbar-bell-hint')
          window.dispatchEvent(event)
        }
      } catch (e) {
        // noop
      }

      // Lembrete periódico enquanto houver notificações
      const remind = () => {
        try {
          const hasAny = this.totalNotifications > 0
          const dropdownOpen = this.showNotifications
          if (hasAny && !dropdownOpen) {
            const event = new Event('navbar-bell-hint')
            window.dispatchEvent(event)
          }
        } catch (e) {
          // noop
        }
        this.bellReminderTimer = setTimeout(remind, 60000) // a cada 60s
      }
      this.bellReminderTimer = setTimeout(remind, 60000)
    }
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    if (typeof window !== 'undefined') {
      window.removeEventListener('pos-offline-count', this.handlePosOfflineEvent)
      window.removeEventListener('home-total-pending', this.handleTotalPendingEvent)
      window.removeEventListener('home-local-server-offline', this.handleLocalServerOfflineEvent)
    }
    if (this.bellReminderTimer) clearTimeout(this.bellReminderTimer)
  },

  created() {
    this.user = sessionStorage.getItem('usuarioLogado')
    this.divisionName = sessionStorage.getItem('divisionName')

    if (sessionStorage.getItem('typeUser') == 'organization') {
      this.userType = 'Organização'
    } else if (sessionStorage.getItem('typeUser') == 'company') {
      this.userType = 'Empresa'
    } else {
      this.userType = 'Loja'
    }
  }
}
</script>

<style scoped>
/* Navbar principal */
.modern-navbar {
  /* manter a cor original do sistema vinda do variant="primary" */
  background: transparent;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: none;
  height: 90px; /* altura original para não quebrar o sidebar */
  padding: 0 1.5rem;
  position: relative;
  overflow: visible; /* Alterado para permitir o dropdown */
  z-index: 1000;
}

.modern-navbar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* overlay sutil mantendo a cor base do tema */
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%);
  pointer-events: none;
}

/* Layout da navbar */
.navbar-left,
.navbar-center,
.navbar-right {
  display: flex;
  align-items: center;
  height: 100%;
}

.navbar-left {
  flex: 0 0 auto;
  gap: 1.5rem;
}

.navbar-center {
  flex: 1;
  justify-content: center;
}

.navbar-right {
  flex: 0 0 auto;
  margin-left: auto;
}

/* Notification bell */
.notification-wrapper {
  position: relative;
  margin-left: 16px; /* espaço do bloco do usuário */
}

.notification-bell {
  position: relative;
  margin-right: 8px;
  color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  font-size: 1.6rem; /* maior */
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.notification-bell:hover {
  transform: translateY(-1px);
  opacity: 0.9;
}

.notification-bell .badge {
  position: absolute;
  top: -8px;
  right: -10px;
  background: #ff4d4f;
  color: #fff;
  border-radius: 12px;
  padding: 0 8px;
  font-size: 0.8rem; /* maior e mais legível */
  line-height: 18px;
  min-width: 22px;
  min-height: 18px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0;
  font-variant-numeric: tabular-nums; /* evita deformações dos dígitos */
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  z-index: 1;
}

.notification-bell.has-notifications {
  color: #ffd666; /* destaque sutil quando há notificações */
  animation: bell-wiggle 1.2s ease-in-out infinite; /* anima somente se houver notificações */
}

.notification-bell.bell-hint {
  animation: bell-wiggle 0.8s ease-in-out infinite, pulse-dot 1.2s infinite;
  color: #ffd666;
}

.bell-toast {
  position: absolute;
  top: calc(100% + 20px);
  right: 0;
  /* background: linear-gradient(135deg, rgba(254, 92, 58, 0.65), rgba(255, 138, 101, 0.65)); */
  background: linear-gradient(135deg, rgba(254, 92, 58, 0.65), rgba(247, 132, 109, 0.65));

  color: #fff;
  padding: 20px 32px;
  border-radius: 20px;
  font-size: 1rem;
  font-weight: 600;
  /* box-shadow: 0 12px 40px rgba(254, 91, 58, 0.575); */
  transform: translateY(-8px);
  opacity: 0;
  animation: toast-slide-in 0.4s ease forwards;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  z-index: 1000;
  min-width: 420px;
  text-align: center;
  justify-content: center;
  border: 2px solid rgba(254, 91, 58, 0.733);
  backdrop-filter: blur(10px);
  white-space: nowrap;
}

@keyframes toast-slide-in {
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* pulso extra para chamar atenção */
.bell-toast {
  animation: toast-slide-in 0.4s ease forwards, toast-pulse 2s ease-in-out 0.4s 1;
}

.notification-icon {
  animation: icon-bounce 1.5s ease-in-out infinite, icon-glow 2s ease-in-out infinite;
  font-size: 1.2rem;
  color: #ffd700;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
}

@keyframes toast-pulse {
  0% {
    transform: translateY(0) scale(1);
    box-shadow: 0 12px 40px rgba(254, 92, 58, 0.4);
  }
  50% {
    transform: translateY(0) scale(1.02);
    box-shadow: 0 16px 50px rgba(254, 92, 58, 0.5);
  }
  100% {
    transform: translateY(0) scale(1);
    box-shadow: 0 12px 40px rgba(254, 92, 58, 0.4);
  }
}

@keyframes icon-bounce {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-3px) rotate(-5deg);
  }
  50% {
    transform: translateY(-6px) rotate(0deg);
  }
  75% {
    transform: translateY(-3px) rotate(5deg);
  }
}

@keyframes icon-glow {
  0%,
  100% {
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
    transform: scale(1);
  }
  50% {
    text-shadow: 0 0 20px rgba(255, 215, 0, 0.9), 0 0 30px rgba(255, 215, 0, 0.4);
    transform: scale(1.1);
  }
}

.notification-bell.has-notifications::after {
  content: '';
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #ff4d4f;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.6);
  animation: pulse-dot 1.6s infinite;
}

@keyframes bell-wiggle {
  0%,
  100% {
    transform: rotate(0deg);
  }
  15% {
    transform: rotate(-12deg);
  }
  30% {
    transform: rotate(10deg);
  }
  45% {
    transform: rotate(-8deg);
  }
  60% {
    transform: rotate(6deg);
  }
  75% {
    transform: rotate(-3deg);
  }
}

@keyframes pulse-dot {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 77, 79, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0);
  }
}

.notifications-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  width: 350px;
  z-index: 1001;
  padding: 0.5rem 0;
}

.notifications-header {
  padding: 0.5rem 1rem;
  color: #fff;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.notifications-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: white;
  cursor: pointer;
  transition: background 0.2s ease;
}

.notifications-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(254, 92, 58, 0.2);
}

.notifications-item:hover::after {
  content: '\f002';
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  /* background: linear-gradient(135deg, #f52c03, #f39a7faf); */
  /* background: linear-gradient(135deg, #fe5c3a, #ff3c0071); */
  background: linear-gradient(135deg, #fe5c3a, #ff8a65);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  animation: lupaAppear 0.4s ease forwards;
  pointer-events: none;
  box-shadow: 0 4px 15px rgba(254, 92, 58, 0.4);
}

@keyframes lupaAppear {
  0% {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  50% {
    opacity: 1;
    transform: translateY(-50%) scale(1.1);
  }
  100% {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.notifications-item .item-left i {
  color: #fe5c3a;
}

.notifications-item .item-title {
  font-weight: 600;
}

.notifications-item .item-subtitle {
  font-size: 0.85rem;
  opacity: 0.9;
}

.notifications-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 1rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
}

.notifications-empty .empty-icon {
  font-size: 2rem;
  color: #4caf50;
  opacity: 0.8;
}

.notifications-empty .empty-text {
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
}

/* Botão do sidebar */
.sidebar-toggle {
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  width: 45px;
  height: 45px;
  border-radius: 12px;
}

.sidebar-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

/* Container do logo */
.logo-container {
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
}

.logo-container:hover {
  transform: scale(1.05);
}

.logo-image {
  height: 50px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  transition: all 0.3s ease;
}

.logo-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.logo-container:hover .logo-glow {
  opacity: 1;
}

/* Widget do calendário */
.calendar-widget {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 1rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.calendar-widget:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  background: rgba(255, 255, 255, 0.15);
}

.calendar-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.date-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.day-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #fe5c3a;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  line-height: 1;
}

.calendar-icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

.date-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.month-year,
.time-day {
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}

.time-day {
  font-size: 0.8rem;
  opacity: 0.9;
}

/* Container do perfil do usuário */
.user-profile-container {
  position: relative;
}

/* Perfil do usuário */
.user-profile {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 0.75rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  cursor: pointer;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.user-avatar {
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #fe5c3a, #ff8a65);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(254, 92, 58, 0.4);
}

.user-avatar i {
  font-size: 1.5rem;
  color: white;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.dropdown-arrow {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  margin-left: 0.5rem;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.user-name {
  color: white;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  /* permitir até 2 linhas com ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  max-width: 320px; /* aumentado para caber nomes grandes */
}

.user-details {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
}

.user-type,
.division-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px; /* aumentado para exibir mais texto */
}

.separator {
  color: rgba(255, 255, 255, 0.6);
  font-weight: bold;
}

/* Dropdown menu */
.user-dropdown-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  width: 300px;
  z-index: 9999;
  padding: 0.5rem 0;
  transform-origin: top right;
  animation: fadeInUp 0.3s ease-out;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dropdown-item:hover {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  transform: translateX(5px);
}

.dropdown-item i {
  color: #fe5c3a;
  font-size: 1rem;
}

.dropdown-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0.5rem 0;
}

.dropdown-arrow {
  transition: transform 0.3s ease;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-user-brief {
  display: none;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem 0.5rem 1rem;
}

.dropdown-user-brief .brief-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dropdown-user-brief .brief-name {
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-user-brief .brief-details {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Responsividade */
@media (max-width: 1200px) {
  .calendar-widget {
    padding: 0.75rem 1rem;
  }

  .day-number {
    font-size: 2rem;
  }

  .month-year,
  .time-day {
    font-size: 0.8rem;
  }

  .user-profile {
    padding: 0.5rem 1rem;
  }

  .user-name {
    font-size: 0.9rem;
    max-width: 240px; /* ampliado no breakpoint */
  }

  .user-type,
  .division-name {
    max-width: 160px; /* ampliado no breakpoint */
  }
}

@media (max-width: 992px) {
  .navbar-center {
    display: none;
  }

  .logo-image {
    height: 40px;
  }

  .sidebar-toggle {
    width: 40px;
    height: 40px;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
  }

  .user-avatar i {
    font-size: 1.25rem;
  }
}

@media (max-width: 768px) {
  .modern-navbar {
    padding: 0 0.75rem;
  }

  .navbar-left {
    gap: 1rem;
  }

  .logo-image {
    height: 35px;
  }

  .sidebar-toggle {
    width: 35px;
    height: 35px;
    border-radius: 10px;
  }

  .user-profile {
    padding: 0.5rem 0.75rem;
    gap: 0.75rem;
  }

  .user-name {
    font-size: 0.85rem;
    max-width: 180px; /* ampliado no breakpoint */
  }

  .user-details {
    font-size: 0.75rem;
  }

  .user-type,
  .division-name {
    max-width: 120px; /* ampliado no breakpoint */
  }
}

@media (max-width: 576px) {
  .logo-image {
    height: 30px;
  }

  .sidebar-toggle {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }

  .user-profile {
    padding: 0.35rem 0.5rem;
    gap: 0.4rem;
  }

  .user-avatar {
    width: 34px;
    height: 34px;
  }

  .user-avatar i {
    font-size: 1rem;
  }

  /* Evitar que as infos de usuário quebrem a altura da navbar no mobile */
  .user-info {
    display: none;
  }
  .dropdown-arrow {
    margin-left: 0;
  }
  .dropdown-user-brief {
    display: flex;
  }
}

@media (max-width: 450px) {
  .modern-navbar {
    height: 72px; /* mantém navbar compacta em celulares menores */
  }
}

/* Animações */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modern-navbar > * {
  animation: fadeInUp 0.6s ease-out;
}

.modern-navbar > *:nth-child(2) {
  animation-delay: 0.1s;
}

.modern-navbar > *:nth-child(3) {
  animation-delay: 0.2s;
}

/* Transições do dropdown */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}
</style>
